import React from 'react'
import Link from 'next/link'
import { useTranslation } from 'next-i18next'
import styled from 'styled-components'
import { equals } from 'ramda'
import { Inline, Text } from '@machobear/design-system'
import toInteger from 'lodash/toInteger'

import { breakpoints, colors, fontWeights } from '@/styles'
import BlurredText from '@/components/blurred-text'
import ListingSection from '@/components/listing-section'
import { ParagraphRegular } from '@/components/text'

import { formatFeatures, formatUtilities } from './utils'

type Props = {
  isEntireUnit: boolean
  unitFeatures: {
    name: string
    txt_id: string
    type: string
  }[]
  customFeatures: {
    name: string
    type: string
  }[]
  unitUtilities: {
    name: string
    txt_id: string
    type: string
  }[]
  customUtilities: {
    name: string
    type: string
  }[]
  isHouse: boolean
  smoking: string
  pets: {
    allowed: string
    cats: string
    dogs: string
  }
  building: any[]
  buildingUrl?: string
  buildingType: string
  blurred: boolean
}

type FeatureType = {
  id: string
  name: string
}

const InnerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  justify-content: space-between;
  flex-wrap: wrap;
  width: 100%;
  background: ${colors.background};
  border-radius: 8px;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    gap: 0;
  }
`

const DetailRow = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  padding: 12px 0;
  &:not(:last-child) {
    border-bottom: 1px solid #eee;
  }

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    grid-template-columns: 1fr;
    border-bottom: none;
    padding: 0;
  }
`

const DetailItem = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    gap: 16px;
    padding: 12px 0;
    &:not(:last-child) {
      border-bottom: 1px solid #eee;
    }
  }
`

const PetsRow = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: 16px;
  padding: 12px 0;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    grid-template-columns: 1fr 1fr;
  }

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    display: flex;
    justify-content: space-between;
  }
`

const PetsItem = styled.div`
  grid-column: span 3;
  gap: 16px;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    grid-column: unset;
  }

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    min-width: calc(50% - 8px);
  }
`

const PetsItems = styled.div`
  display: flex;
  flex-direction: column;
`

const DetailText = styled(ParagraphRegular)`
  color: ${colors.grey32};
  font-weight: 500;
`

const DetailTextBold = styled(DetailText)`
  color: ${colors.black};
  font-weight: 600;
`

const SubTitle = styled(ParagraphRegular)`
  font-size: 18px;
  line-height: 24px;
  color: ${colors.black};
  font-weight: 700;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    font-size: 14px;
    line-height: 20px;
  }
`

const LargeSubTitle = styled(SubTitle)`
  font-size: 20px;
  line-height: 28px;
  color: ${colors.grey08};

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    font-size: 16px;
    line-height: 20px;
  }
`

const Section = styled.div`
  background: ${colors.background};
  border-radius: 8px;
  display: flex;
  width: 100%;
  flex-direction: column;
  padding: 24px;
  gap: 24px;
`

const BuildingLink = styled.a`
  color: ${colors.link};
  font-weight: ${fontWeights.medium};
  font-size: 16px;
  line-height: 24px;
  cursor: pointer;
  width: max-content;
  text-decoration: none;
`

const AmenityGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    grid-template-columns: 1fr;
  }
`

const AmenitySectionTitle = styled(ParagraphRegular)`
  color: ${colors.grey08};
  font-weight: 600;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    margin-top: 12px;
  }
`

const AmenityText = styled(ParagraphRegular)`
  color: ${colors.grey24};
  font-weight: 500;
  padding: 12px 0;
  position: relative;

  &:after {
    content: '';
    display: block;
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    border-bottom: solid 1px #eee;
  }

  &:nth-last-child(1),
  &:nth-last-child(2):nth-child(odd) {
    &:after {
      border-bottom: 0;
    }
  }

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    &:nth-last-child(2):nth-child(odd) {
      &:after {
        border-bottom: solid 1px #eee;
      }
    }
  }
`

const equalsOne = equals('1')

const ListingAmenities = ({
  isEntireUnit,
  unitFeatures,
  customFeatures,
  unitUtilities,
  customUtilities,
  building,
  buildingUrl,
  buildingType,
  isHouse,
  smoking,
  pets,
  blurred
}: Props) => {
  const { t } = useTranslation()

  const features = formatFeatures(
    t,
    isEntireUnit,
    unitFeatures,
    customFeatures
  ) as any

  const utilities = formatUtilities(
    t,
    isEntireUnit,
    unitUtilities,
    customUtilities
  ) as any

  const isPetAllowed = (value: string) => {
    return !!value && toInteger(value) > 0
  }

  const catAndDogText = (cats: string, dogs: string) => {
    const catCount = t('listing:b.listing.view.cats_allowed_count.text', {
      defaultValue: '{{count}} cats',
      count: toInteger(cats)
    })

    const dogCount = t('listing:b.listing.view.dogs_allowed_count.text', {
      defaultValue: '{{count}} dogs',
      count: toInteger(dogs)
    })

    return t('listing:b.listing.view.pets_allowed_count.text', {
      defaultValue: '{{ catCount }} and {{ dogCount }} maximum',
      catCount,
      dogCount
    })
  }

  const getPetsText = () => {
    const { allowed, cats, dogs } = pets
    const allowPets = equalsOne(allowed)
    const allowCats = isPetAllowed(cats)
    const allowDogs = isPetAllowed(dogs)
    const petLimitations = allowPets && (allowCats || allowDogs)

    if (petLimitations) {
      return (
        <PetsItems>
          <DetailTextBold>
            {t(
              'listing:b.listing.view.allowed_with_restrictions.text',
              'Allowed with restrictions'
            )}
          </DetailTextBold>
          <DetailTextBold>{catAndDogText(cats, dogs)}</DetailTextBold>
        </PetsItems>
      )
    }
    return (
      <DetailTextBold>
        {allowPets
          ? t('listing:b.listing.view.allowed.text', 'Allowed')
          : t('listing:b.listing.view.not_allowed.text', 'Not allowed')}
      </DetailTextBold>
    )
  }

  const buildingExists = building && building.length > 0
  const hideUnitText = isHouse || buildingType === 'townhouse'

  return (
    <ListingSection
      showBottomSeparator
      title={t('listing:b.listing.view.amenities.text', 'Amenities')}
    >
      <Section>
        {!hideUnitText && (
          <SubTitle>{t('listing:b.building.view.unit.text', 'Unit')}</SubTitle>
        )}
        <InnerWrapper>
          {isEntireUnit ? (
            <AmenityGrid>
              {features.map((amenity: FeatureType) =>
                !blurred ? (
                  <AmenityText key={amenity.id}>{amenity.name}</AmenityText>
                ) : (
                  <Inline space="xsmall" key={amenity.id} alignY={'center'}>
                    <Text>+</Text>
                    <BlurredText variant={{ weight: 'medium' }}>
                      ****
                    </BlurredText>
                  </Inline>
                )
              )}
            </AmenityGrid>
          ) : (
            <>
              <AmenitySectionTitle>
                {t('listing:b.listing_features.view.private.label', 'Private')}
              </AmenitySectionTitle>
              <AmenityGrid>
                {features.private.map((amenity: FeatureType) => (
                  <AmenityText key={amenity.id}>{amenity.name}</AmenityText>
                ))}
              </AmenityGrid>
              <AmenitySectionTitle>
                {t('listing:b.listing_features.view.shared.label', 'Shared')}
              </AmenitySectionTitle>
              <AmenityGrid>
                {features.shared.map((amenity: FeatureType) => (
                  <AmenityText key={amenity.id}>{amenity.name}</AmenityText>
                ))}
              </AmenityGrid>
            </>
          )}
        </InnerWrapper>
      </Section>
      {!isHouse && (
        <Section>
          <SubTitle>
            {t('listing:b.listing.navigation.building.text', 'Building')}
          </SubTitle>
          <InnerWrapper>
            {buildingExists ? (
              <AmenityGrid>
                {building.map(amenity =>
                  !blurred ? (
                    <AmenityText key={amenity.txt_id}>
                      {`+ ${t(
                        `basic:b.building.amenities.${amenity.txt_id}.text`
                      )}`}
                    </AmenityText>
                  ) : (
                    <Inline space="xsmall" key={amenity.id} alignY={'center'}>
                      <Text>+</Text>
                      <BlurredText variant={{ weight: 'medium' }}>
                        ****
                      </BlurredText>
                    </Inline>
                  )
                )}
              </AmenityGrid>
            ) : !blurred ? (
              <AmenityText>
                {t('listing:b.listing.view.no_features.text', 'None available')}
              </AmenityText>
            ) : (
              <BlurredText variant={{ weight: 'medium' }}>****</BlurredText>
            )}
          </InnerWrapper>
          {buildingUrl && !blurred ? (
            <Link href={buildingUrl} passHref legacyBehavior>
              <BuildingLink id="listing-details-view-building-details-click">
                {t(
                  'listing:b.listing.view.view_building_details.link',
                  'View building details'
                )}
              </BuildingLink>
            </Link>
          ) : buildingUrl && blurred ? (
            <Text tone="disabled">
              {t(
                'listing:b.listing.view.view_building_details.link',
                'View building details'
              )}
            </Text>
          ) : null}
        </Section>
      )}
      <LargeSubTitle>
        {t('listing:b.listing.view.utilities.title', 'Utilities')}
      </LargeSubTitle>
      <Section>
        <InnerWrapper>
          {isEntireUnit ? (
            <AmenityGrid>
              {utilities.map((utility: FeatureType) =>
                !blurred ? (
                  <AmenityText key={utility.id}>{utility.name}</AmenityText>
                ) : (
                  <Inline space="xsmall" key={utility.id} alignY={'center'}>
                    <Text>+</Text>
                    <BlurredText variant={{ weight: 'medium' }}>
                      ****
                    </BlurredText>
                  </Inline>
                )
              )}
            </AmenityGrid>
          ) : (
            <>
              <AmenitySectionTitle>
                {t(
                  'listing:b.listing_features.view.included.label',
                  'Included'
                )}
              </AmenitySectionTitle>
              <AmenityGrid>
                {utilities.included.map((utility: FeatureType) =>
                  blurred ? (
                    <Inline space="xsmall" key={utility.id} alignY={'center'}>
                      <Text>+</Text>
                      <BlurredText variant={{ weight: 'medium' }}>
                        ****
                      </BlurredText>
                    </Inline>
                  ) : (
                    <AmenityText key={utility.id}>{utility.name}</AmenityText>
                  )
                )}
              </AmenityGrid>
              <AmenitySectionTitle>
                {t('listing:b.listing_features.view.shared.label', 'Shared')}
              </AmenitySectionTitle>
              <AmenityGrid>
                {utilities.shared.map((utility: FeatureType) =>
                  blurred ? (
                    <Inline space="xsmall" key={utility.id} alignY={'center'}>
                      <Text>+</Text>
                      <BlurredText variant={{ weight: 'medium' }}>
                        ****
                      </BlurredText>
                    </Inline>
                  ) : (
                    <AmenityText key={utility.id}>{utility.name}</AmenityText>
                  )
                )}
              </AmenityGrid>
              <AmenitySectionTitle>
                {t('listing:b.listing_features.view.metered.label', 'Metered')}
              </AmenitySectionTitle>
              <AmenityGrid>
                {utilities.metered.map((utility: FeatureType) =>
                  blurred ? (
                    <Inline space="xsmall" key={utility.id} alignY={'center'}>
                      <Text>+</Text>
                      <BlurredText variant={{ weight: 'medium' }}>
                        ****
                      </BlurredText>
                    </Inline>
                  ) : (
                    <AmenityText key={utility.id}>{utility.name}</AmenityText>
                  )
                )}
              </AmenityGrid>
            </>
          )}
        </InnerWrapper>
      </Section>
      <LargeSubTitle>
        {t('listing:b.listing.view.property_rules.text', 'Property Rules')}
      </LargeSubTitle>
      <Section>
        <InnerWrapper>
          <DetailRow>
            <DetailItem>
              <DetailText>
                {t('listing:b.listing.view.smoking.text', 'Smoking')}
              </DetailText>
              {!blurred ? (
                <DetailTextBold>{smoking}</DetailTextBold>
              ) : (
                <BlurredText variant={{ weight: 'medium' }}>****</BlurredText>
              )}
            </DetailItem>
          </DetailRow>
          <PetsRow>
            <DetailText>
              {t('listing:b.listing.view.pets.text', 'Pets')}
            </DetailText>
            {!blurred ? (
              <PetsItem>{getPetsText()}</PetsItem>
            ) : (
              <BlurredText variant={{ weight: 'medium' }}>****</BlurredText>
            )}
          </PetsRow>
        </InnerWrapper>
      </Section>
    </ListingSection>
  )
}

export default ListingAmenities
